import { useDroppable } from "@dnd-kit/core";
import { HomeIcon } from "@heroicons/react/outline";
import { ListCollapse } from "lucide-react";

import { ExpandableSectionHeader } from "./ExpandableSectionHeader";

import { useDragDrop } from "@/components/UnifiedRegistry/DragAndDrop/DragDropProvider";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { cn } from "@/lib/utils";

interface HomeRowProps {
  isActive: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  onNavigate: () => void;
  onCollapseAllFolders?: () => void;
  hasExpandedFolders?: boolean;
  /** Sticky top offset in px (Favorites header height when stacking). */
  stickyTopPx?: number;
}

export const HomeRow = ({
  isActive,
  isExpanded,
  onToggle,
  onNavigate,
  onCollapseAllFolders,
  hasExpandedFolders = false,
  stickyTopPx = 0,
}: HomeRowProps) => {
  const { draggedOverFolderId } = useDragDrop();
  const { isOver, setNodeRef } = useDroppable({ id: "root-drop-zone" });
  const isDropActive = isOver && draggedOverFolderId === -1;

  const collapseAction =
    onCollapseAllFolders && hasExpandedFolders ? (
      <BasicTooltip content="Collapse all folders">
        <button
          type="button"
          aria-label="Collapse all folders"
          className={cn(
            "flex h-5 w-5 items-center justify-center rounded-sm text-gray-400",
            "opacity-0 transition-opacity duration-150 hover:bg-gray-200 hover:text-gray-700",
            "group-focus-within/section:opacity-100 group-hover/section:opacity-100",
          )}
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            onCollapseAllFolders();
          }}
          onMouseDown={e => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <ListCollapse className="h-3.5 w-3.5" strokeWidth={2} />
        </button>
      </BasicTooltip>
    ) : null;

  return (
    <ExpandableSectionHeader
      label="Home"
      icon={<HomeIcon className="h-3.5 w-3.5" />}
      isExpanded={isExpanded}
      onToggle={onToggle}
      isActive={isActive}
      onNavigate={onNavigate}
      sticky
      stickyTopPx={stickyTopPx}
      stickyZIndex={10}
      dropRef={setNodeRef}
      isDropActive={isDropActive}
      trailingAction={collapseAction}
    />
  );
};
